"use client";
import { useEffect, useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { Share } from "lucide-react";
import { AppVersion } from "./App-version";
import { Button } from "./button";
import { CreatePublicLinkChatHistory } from "./CreatePublicLinkChatHistory";
import { useStateHistoryChatPage } from "@/app/store/chat";

import { useAuth, useMediaQuery } from "@/app/hooks";
import { UsageIndicator } from "./UsageIndicator";
import { cn } from "@/app/lib/utils";
import { TaiZenLogo } from "./TaiZenLogo";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";
import { CHAT_ROUTE_SEGMENT, SHARE_ROUTER_SEGMENT } from "@/app/lib/constants";
import { locales, type Locale } from "@/i18n/settings";
import { ModeToggle } from "./Button-mode-toggle";
import { LanguageSwitcher } from "./Language-switcher";
import { ProfileMenu } from "./Profile-menu";
import { UserSettingsDialog } from "./UserSettingsDialog";

const getRouteSegments = (pathname: string): string[] => {
   const segments = pathname.split("/").filter(Boolean);
   return locales.includes(segments[0] as Locale) ? segments.slice(1) : segments;
};

export const isAdminRoute = (pathname: string | null): boolean => {
   if (!pathname) return false;
   const routeSegments = getRouteSegments(pathname);
   return routeSegments[0] === "auth" && routeSegments[1] === "admin";
};

export const shouldHideTopHeader = (pathname: string | null): boolean => {
   if (!pathname) return false;
   if (isAdminRoute(pathname)) return true;
   const [firstSegment] = getRouteSegments(pathname);
   return firstSegment === "help" || firstSegment === "profile";
};

export const shouldHideHeaderUserControls = (pathname: string | null): boolean => {
   if (!pathname) return true;
   if (shouldHideTopHeader(pathname)) return true;
   const routeSegments = getRouteSegments(pathname);
   if (routeSegments.length === 0) return true;
   return (
      routeSegments.includes(CHAT_ROUTE_SEGMENT) ||
      routeSegments.includes(SHARE_ROUTER_SEGMENT)
   );
};

type Props = {
   className?: string;
};
export const Header = ({ className = "" }: Props) => {
   const translate = useTranslations("Home");
   const { historyItem, selectedModel, loadingChat, hasActiveGeneration } =
      useStateHistoryChatPage();
   const { selectedBaseKnowledge } = useAiSourceSelector();
   const isMobile = useMediaQuery("(max-width: 768px)");
   const isTablet = useMediaQuery("(min-width: 769px) and (max-width: 1024px)");
   const [showShareDialog, setShowShareDialog] = useState(false);
   const { user, isInitializing, isSharedLoginAuthenticating } = useAuth();
   const [isScrolled, setIsScrolled] = useState(false);
   const pathname = usePathname();
   const [autoSettingsOpen, setAutoSettingsOpen] = useState(false);

   useEffect(() => {
      if (isInitializing || isSharedLoginAuthenticating) {
         setAutoSettingsOpen(false);
         return;
      }

      if (
         user &&
         user.projects &&
         user.projects.length > 0 &&
         !user.metaData?.hasAcceptedMemoryPolicy
      ) {
         setAutoSettingsOpen(true);
      } else {
         setAutoSettingsOpen(false);
      }
   }, [isInitializing, isSharedLoginAuthenticating, user]);

   const hideTopHeader = useMemo(() => shouldHideTopHeader(pathname), [pathname]);
   const hideUserControls = useMemo(
      () => shouldHideHeaderUserControls(pathname),
      [pathname]
   );
   const isChatPage = !hideTopHeader && hideUserControls;

   const shareActionsDisabled = Boolean(
      historyItem?.id && (loadingChat || hasActiveGeneration(historyItem.id))
   );

   useEffect(() => {
      if (hideTopHeader) return;

      const handleScroll = () => {
         setIsScrolled(window.scrollY > 10);
      };

      window.addEventListener("scroll", handleScroll);

      return () => {
         window.removeEventListener("scroll", handleScroll);
      };
   }, [hideTopHeader]);

   if (hideTopHeader) {
      return (
         <UserSettingsDialog
            open={autoSettingsOpen}
            onOpenChange={setAutoSettingsOpen}
            isFirstTime={true}
         />
      );
   }

   return (
      <header
         className={cn(
            "fixed top-0 left-0 right-0 z-50 flex items-center justify-between p-3 transition-colors duration-300",
            isScrolled
               ? "bg-background/10 backdrop-blur-[2px] border-b shadow-sm"
               : "bg-transparent",
            className
         )}
      >
         <div className="flex items-center gap-0 min-w-0">
            <div className="relative shrink-0 pb-3">
               <a
                  href="/"
                  className="dark:text-white text-black font-semibold flex items-center"
               >
                  <TaiZenLogo
                     size="xxs"
                     className="border-2 border-spacing-1 rounded-full"
                  />
               </a>
               <div className="absolute left-1/2 -translate-x-1/2 -bottom-0">
                  <AppVersion />
               </div>
            </div>
            {user?.id && (
               <div
                  className={cn(
                     "font-semibold text-primary relative h-12 flex flex-col justify-center mx-1 sm:mx-2 truncate min-w-0",
                     isTablet && "w-[250px]",
                     !isMobile && !isTablet && "w-[350px]"
                  )}
               >
                  <span className="truncate w-full">{selectedBaseKnowledge?.name}</span>
                  <div
                     className={cn(
                        "absolute top-7",
                        className.match(/text-(?:\[[^\]]+\]|[a-z0-9-]+)/i)?.[0] || "",
                        !user?.isAdmin && "hidden"
                     )}
                  >
                     {user?.isAdmin && !isMobile && !isTablet && (
                        <span className="text-gray-500 text-[10px]">
                           <span>model: </span>
                           <code>{selectedModel}</code>
                        </span>
                     )}
                  </div>
               </div>
            )}
         </div>
         <div
            className={cn("flex items-center shrink-0", {
               "gap-1": isMobile,
               "gap-2": !isMobile
            })}
         >
            {isChatPage && historyItem && (
               <div>
                  <Button
                     onClick={() => setShowShareDialog(true)}
                     className="rounded-full"
                     variant="outline"
                     size={isMobile ? "xs" : "sm"}
                  >
                     <Share size={isMobile ? 14 : 16} />
                     {!isMobile && translate("share")}
                  </Button>
                  <CreatePublicLinkChatHistory
                     item={historyItem}
                     isOpen={showShareDialog}
                     setOpen={setShowShareDialog}
                     actionsDisabled={shareActionsDisabled}
                  />
               </div>
            )}
            {!hideUserControls && (
               <>
                  {user?.id && (
                     <Button
                        variant="outline"
                        size={isMobile ? "xs" : "sm"}
                        className={cn("rounded-full cursor-auto", {
                           "px-2": isMobile
                        })}
                     >
                        <UsageIndicator compact />
                     </Button>
                  )}
                  <ModeToggle />
                  <LanguageSwitcher />
                  {user?.id && <ProfileMenu />}
               </>
            )}
         </div>

         <UserSettingsDialog
            open={autoSettingsOpen}
            onOpenChange={setAutoSettingsOpen}
            isFirstTime={true}
         />
      </header>
   );
};
